import { useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { authClient } from '@/api/auth';
import { CosmosMark } from '@/components/CosmosMark';

const SSO_ERROR_MESSAGES: Record<string, string> = {
  unable_to_create_user: 'Your Microsoft account was not found in Autotask. Contact your administrator.',
  account_not_linked: 'Could not link your Microsoft account. Try signing in with email first.',
};

// ── Canvas: exact port of the reference animation ────────────────────────────
// Source extracted by decompressing the bundled Cosmos Logo.html artifact.
// Every constant, formula and seeded-random sequence is copied verbatim so
// the output matches the reference pixel-for-pixel.

const ACCENT: [number, number, number] = [94, 235, 196]; // #5EEBC4

// Hero canvas config (from renderVals → refFor('hero', {...}) in the source)
const CFG = { n: 120, hub: true, lim: 0.42, lw: 0.7, rs: 1.6, boost: 1, R: 0.42, cx: 0.56, cy: 0.48, speed: 0.06 };

interface AnimNode { x: number; y: number; z: number; ph: number; hub: boolean; }

function makeNodes(): AnimNode[] {
  // Park-Miller LCG — exact seed and multiplier from source
  let s = 7;
  const rnd = () => { s = (s * 16807) % 2147483647; return (s - 1) / 2147483646; };

  const ga = Math.PI * (3 - Math.sqrt(5));
  const nodes: AnimNode[] = [];
  for (let i = 0; i < CFG.n; i++) {
    const y = 1 - (i / (CFG.n - 1)) * 2;
    const r = Math.sqrt(1 - y * y);
    const th = ga * i + rnd() * 0.5;   // golden-angle + jitter
    const k = 0.6 + rnd() * 0.42;      // random radial scale (not a unit sphere)
    nodes.push({ x: Math.cos(th) * r * k, y: y * k, z: Math.sin(th) * r * k, ph: rnd() * 6.283, hub: false });
  }
  // Hub node: fixed off-centre position, bobs vertically, no rotation
  if (CFG.hub) nodes.push({ x: -1.25, y: 0.1, z: 0.35, ph: 0, hub: true });
  return nodes;
}

function CosmosNetworkCanvas() {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const nodes = makeNodes();
    const [cr, cg, cbl] = ACCENT;

    // Resize: set canvas pixel dimensions and apply dpr transform so all draw
    // calls use CSS-pixel coordinates (matching the setTransform in the source).
    let cssW = 0, cssH = 0;
    const applySize = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      cssW = canvas.offsetWidth;
      cssH = canvas.offsetHeight;
      canvas.width  = Math.round(cssW * dpr);
      canvas.height = Math.round(cssH * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    applySize();
    const ro = new ResizeObserver(applySize);
    ro.observe(canvas);

    const t0 = performance.now();
    let raf: number;

    const draw = (now: number) => {
      const t = (now - t0) / 1000;

      // Re-apply transform every frame in case a resize reset it
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

      const W = cssW || canvas.offsetWidth;
      const H = cssH || canvas.offsetHeight;

      ctx.globalCompositeOperation = 'source-over';
      ctx.clearRect(0, 0, W, H);
      ctx.globalCompositeOperation = 'lighter';

      // Rotation angles — exact formulas from source
      const ay = t * CFG.speed;
      const ax = -0.38 + Math.sin(t * 0.2) * 0.1;
      const cosY = Math.cos(ay), sinY = Math.sin(ay);
      const cosX = Math.cos(ax), sinX = Math.sin(ax);

      const R  = Math.min(W, H) * CFG.R;
      const CX = W * CFG.cx;
      const CY = H * CFG.cy;

      // Project every node — exact copy of the source draw() map
      const pts = nodes.map((n) => {
        const k = n.hub ? 1 : 1 + Math.sin(t * 0.7 + n.ph) * 0.04;
        let x = n.x * k, y = n.y * k, z = n.z * k;
        if (!n.hub) {
          const x1 = x * cosY + z * sinY, z1 = -x * sinY + z * cosY;
          const y2 = y * cosX - z1 * sinX, z2 =  y * sinX + z1 * cosX;
          x = x1; y = y2; z = z2;
        } else {
          y += Math.sin(t * 0.4) * 0.05; // hub bobs vertically only
        }
        const p = 2.8 / (2.8 - z);
        return { X: CX + x * R * p, Y: CY + y * R * p, x, y, z, d: (z + 1.2) / 2.4, hub: n.hub, ph: n.ph };
      });

      // Lines — exact alpha formula from source (power 1.2, depth weight, hub lim)
      ctx.lineWidth = CFG.lw;
      for (let i = 0; i < pts.length; i++) {
        const a = pts[i];
        for (let j = i + 1; j < pts.length; j++) {
          const q = pts[j];
          const lim = (a.hub || q.hub) ? 1.3 : CFG.lim;
          const dx = a.x - q.x, dy = a.y - q.y, dz = a.z - q.z;
          const d = Math.sqrt(dx * dx + dy * dy + dz * dz);
          if (d >= lim) continue;
          const depth = Math.max(0.12, Math.min(1, (a.d + q.d) / 2));
          const al = Math.min(1, Math.pow(1 - d / lim, 1.2) * (0.2 + 0.6 * depth) * CFG.boost * ((a.hub || q.hub) ? 0.55 : 1));
          ctx.strokeStyle = `rgba(${cr},${cg},${cbl},${al.toFixed(3)})`;
          ctx.beginPath(); ctx.moveTo(a.X, a.Y); ctx.lineTo(q.X, q.Y); ctx.stroke();
        }
      }

      // Nodes — outer glow + brightened inner core (exact from source)
      for (const p of pts) {
        const depth = Math.max(0.1, Math.min(1, p.d));
        const tw = 0.75 + 0.25 * Math.sin(t * 2 + p.ph * 3);
        const r  = (p.hub ? 2.6 : 0.8 + 1.4 * depth) * CFG.rs;

        ctx.fillStyle = `rgba(${cr},${cg},${cbl},${(0.14 * depth * tw).toFixed(3)})`;
        ctx.beginPath(); ctx.arc(p.X, p.Y, r * 3, 0, 6.283); ctx.fill();

        ctx.fillStyle = `rgba(${Math.min(255, cr + 80)},${Math.min(255, cg + 40)},${Math.min(255, cbl + 50)},${Math.min(1, (0.5 + 0.5 * depth) * tw * CFG.boost).toFixed(3)})`;
        ctx.beginPath(); ctx.arc(p.X, p.Y, r, 0, 6.283); ctx.fill();
      }

      raf = requestAnimationFrame(draw);
    };

    raf = requestAnimationFrame(draw);
    return () => { cancelAnimationFrame(raf); ro.disconnect(); };
  }, []);

  return <canvas ref={canvasRef} className="absolute inset-0 h-full w-full" aria-hidden="true" />;
}

// ── Microsoft icon ────────────────────────────────────────────────────────────
function MicrosoftIcon() {
  return (
    <svg viewBox="0 0 21 21" width="16" height="16" aria-hidden="true" style={{ flexShrink: 0 }}>
      <rect x="0"  y="0"  width="10" height="10" fill="#f25022" />
      <rect x="11" y="0"  width="10" height="10" fill="#7fba00" />
      <rect x="0"  y="11" width="10" height="10" fill="#00a4ef" />
      <rect x="11" y="11" width="10" height="10" fill="#ffb900" />
    </svg>
  );
}

// ── Login page ────────────────────────────────────────────────────────────────
export default function LoginPage() {
  const [searchParams] = useSearchParams();
  const errorCode = searchParams.get('error');
  const errorMessage = errorCode
    ? (SSO_ERROR_MESSAGES[errorCode] ?? `Sign-in error: ${errorCode}`)
    : null;

  const signInWithMicrosoft = async () => {
    try {
      await authClient.signIn.social({ provider: 'microsoft', callbackURL: '/' });
    } catch {
      toast.error('Microsoft sign-in failed', { description: 'An unexpected error occurred.' });
    }
  };

  return (
    <div className="grid min-h-full grid-cols-1 lg:grid-cols-[2fr_3fr]" style={{ background: '#0A0F1C' }}>

      {/* ── Left: sign-in panel ─────────────────────────────────────────── */}
      <div
        className="flex flex-col justify-between p-10 xl:px-14"
        style={{ background: '#0A0F1C', borderRight: '1px solid #141C2E' }}
      >
        {/* Logo — existing CosmosMark glyph + Space Grotesk wordmark */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <CosmosMark size={36} className="rounded-[9px]" />
          <span style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 21, fontWeight: 400, letterSpacing: '-0.02em', color: '#F4F7FF' }}>
            Cosmos
          </span>
        </div>

        {/* Heading + sign-in */}
        <div className="mx-auto w-full max-w-sm" style={{ display: 'flex', flexDirection: 'column', gap: 28, alignSelf: 'center' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 44, fontWeight: 300, letterSpacing: '-0.03em', lineHeight: 1.08, color: '#F4F7FF' }}>
              Welcome back to Cosmos
            </div>
            <div style={{ fontSize: 15, lineHeight: 1.55, color: '#8F9BBA' }}>
              Tickets, clients and opportunities for the Synfo service desk, all in one connected view.
            </div>
          </div>

          {errorMessage && (
            <div style={{ borderRadius: 10, border: '1px solid rgba(220,80,80,0.3)', background: 'rgba(220,80,80,0.08)', padding: '12px 16px', fontSize: 13, lineHeight: 1.5, color: '#FF8C8C' }}>
              {errorMessage}
            </div>
          )}

          {/* Microsoft SSO — the only sign-in method */}
          <button
            type="button"
            onClick={signInWithMicrosoft}
            style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, height: 46, background: '#F4F7FF', color: '#0A0F1C', borderRadius: 10, border: 'none', fontSize: 14, fontWeight: 600, fontFamily: 'inherit', cursor: 'pointer', letterSpacing: '-0.01em' }}
          >
            <MicrosoftIcon />
            Continue with Microsoft
          </button>
        </div>

        {/* Footer */}
        <p style={{ fontSize: 12, lineHeight: 1.5, color: '#5D6987' }}>
          By signing in you agree to Synfo&apos;s acceptable use policy.
        </p>
      </div>

      {/* ── Right: animated network ─────────────────────────────────────── */}
      <div
        className="relative hidden overflow-hidden lg:block"
        style={{ background: 'radial-gradient(90% 80% at 60% 45%, #0D2A3A 0%, #071521 45%, #03080F 100%)' }}
      >
        <CosmosNetworkCanvas />

        {/* Bottom-left tagline — exact position from reference (left:96px, bottom:56px) */}
        <div style={{ position: 'absolute', left: 96, bottom: 56, display: 'flex', flexDirection: 'column', gap: 6 }}>
          <div style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 22, fontWeight: 300, letterSpacing: '-0.01em', color: '#F4F7FF' }}>
            Everything, connected.
          </div>
          <div style={{ fontSize: 13, color: '#8F9BBA' }}>
            Built by Synfo for Synfo.
          </div>
        </div>
      </div>
    </div>
  );
}
